import { TFunction } from 'react-i18next';

import {
  Agreement,
  ProductParametersKey,
  SimulationStatusTextDescription,
  BIGHistory,
  IPlanData,
  PlanStatusType,
  AdditionalCostsType,
  PaymentDetails
} from '../agreements/agreements.model';
import {
  ContractDTO,
  ContractPaymentDetails,
  ProductParameters as ContractProductParameters,
  Installment as ContractInstallment,
  EconomicInformation,
  EconomicInformationAction,
  ContractDetails
} from './contracts.model';
import { CurrencyType } from '../../utils/formatCurrency/formatCurrency.model';

/**
 * DUPLICATED TYPES FOUND IN agreements.model.ts AND contracts.model.ts:
 * - WelcomeKisaStatus
 * - ProductType
 * - ProductTypeEnum
 * - ProductName
 * - PayerType
 * - SimulationStatus
 * - SimulationStatusTextDescription
 * - SimulationStatusEnum
 * - PhaseStatusTypes
 * - AdditionalCostTypes
 * - PhaseSegmentation
 * - AdditionalCostsType
 * - AdditionalCostModalText
 * - IBankAccount
 * - PaymentDetails
 * - PaymentDetailsResponse
 * - PlanStatusType (Agreement) / InstallmentStatus (Contract) - similar values
 * - BIGHistory - different property names: status/entries (Agreement) vs state/actions (Contract)
 * - BIGHistoryEntry (Agreement) / EconomicInformationAction (Contract) - different property names: action/date/route (Agreement) vs date/action/officeName (Contract)
 * - BIGHistoryActionType
 * - BIGHistoryRouteType
 * - BIGHistoryStatus
 */

export const mapPaymentsDetails = (
  payments: ContractPaymentDetails
): PaymentDetails => ({
  bankAccounts: payments.bankAccounts.map((details) => ({
    accountNumber: details.bankAccountNumber,
    accountCurrency: details.currency,
    swiftCode: details.swift
  })),
  currentCreditorName: payments.currentLenderName,
  paymentCode: payments.paymentCode
});

export const mapInstallments = (
  installments: ContractInstallment[]
): IPlanData[] =>
  (installments || []).map((installment) => ({
    id: installment.installmentNumber,
    date: installment.dueDate,
    amount: installment.amount.amount,
    currency: (installment.amount.currency as CurrencyType) ?? 'NONE',
    status: (installment.status === 'PARTIALLY_PAID'
      ? 'UNPAID'
      : installment.status) as PlanStatusType // mapping PARTIALLY_PAID to UNPAID as it's not present in PlanStatusType
  }));

export const mapProductParameters = (
  parameters: ContractProductParameters
): { key: ProductParametersKey; value: string }[] | undefined => {
  const params: { key: ProductParametersKey; value: string }[] = [];
  if (!parameters) return undefined;
  if (parameters.installmentCount) {
    params.push({
      key: ProductParametersKey.INSTALLMENTS_COUNT,
      value: parameters.installmentCount.toString()
    });
  }
  if (parameters.lastInstallmentPaymentDate) {
    params.push({
      key: ProductParametersKey.LAST_INSTALLMENT_PAYMENT,
      value: parameters.lastInstallmentPaymentDate
    });
  }
  if (parameters.installmentAmount) {
    params.push({
      key: ProductParametersKey.INSTALLMENTS_AMOUNT,
      value: parameters.installmentAmount.amount.toString()
    });
  }
  if (parameters.firstInstallmentPaymentDate) {
    params.push({
      key: ProductParametersKey.FIRST_INSTALLMENT_PAYMENT,
      value: parameters.firstInstallmentPaymentDate
    });
  }
  if (parameters.lastInstallmentAmount) {
    params.push({
      key: ProductParametersKey.LAST_INSTALLMENT_AMOUNT,
      value: parameters.lastInstallmentAmount.amount.toString()
    });
  }
  if (parameters.prerequisitePaymentAmount) {
    params.push({
      key: ProductParametersKey.PREREQUISITE_PAYMENT_AMOUNT,
      value: parameters.prerequisitePaymentAmount.amount.toString()
    });
  }
  if (parameters.prerequisitePaymentDate) {
    params.push({
      key: ProductParametersKey.PREREQUISITE_PAYMENT_DATE,
      value: parameters.prerequisitePaymentDate
    });
  }
  if (parameters.remissionAmount) {
    params.push({
      key: ProductParametersKey.REMISSION_AMOUNT,
      value: parameters.remissionAmount.amount.toString()
    });
  }
  if (parameters.repaidInstallmentCount !== 0) {
    params.push({
      key: ProductParametersKey.REPAID_INSTALLMENT_COUNT,
      value: parameters.repaidInstallmentCount.toString()
    });
  }

  return params;
};

const mapBigHistory = (
  economicInformation: EconomicInformation | null
): BIGHistory => ({
  status: economicInformation?.state || 'NULL',
  entries: (economicInformation?.actions || []).map(
    (action: EconomicInformationAction) => ({
      action: action.action,
      date: action.date,
      route: action.officeName
    })
  )
});

const additionalCostType = (
  amountOutdated: boolean,
  contractStage: string
): AdditionalCostsType => {
  if (!amountOutdated) {
    return AdditionalCostsType.NONE;
  }

  switch (contractStage) {
    case 'COURT':
      return AdditionalCostsType.COURT_PROCESS_COST;
    case 'BAILIFF_EXECUTION':
      return AdditionalCostsType.BAILIFF_ENFORCEMENT_COST;
    default:
      return AdditionalCostsType.NONE;
  }
};

export const mapContractToAgreement = (contract: ContractDTO): Agreement => {
  const overduePaymentsAmount =
    contract.productData?.overduePaymentsAmount?.amount || 0;

  return {
    generalData: {
      // TODO: Verify if these fields exist in the new details but under different names
      offerAvailable: null, // offerAvailable (offer) taken from websocket, deprecated
      fromUgodowi: false, // TODO: fromUgodowi doesn't appear in contract endpoint, deprecated
      creditorId: null as any, // TODO: unused, deprecated
      lastSettledPayment: null as any, // TODO: Move to notifications endpoint
      overduePaymentsAmount,
      agreementId: contract.sourceContractId,
      originalCreditorName: contract.lenderName,
      currency: contract.currency ?? 'NONE',
      currentCreditorName: contract.lenderName,
      totalDebtAmountByCurrency: contract.totalDebtAmountByCurrency ?? {
        NONE: 0
      },
      totalDebtAmountWithoutRemissionByCurrency:
        contract.totalDebtAmountWithoutRemissionByCurrency,
      waitingPaymentsAmountByCurrency: contract.waitingPaymentsAmountByCurrency,
      totalDebtAmountInSystemCurrency:
        contract.totalDebtAmountInSystemCurrency?.amount ?? 0,
      totalDebtAmountInSystemCurrencyValue:
        contract.totalDebtAmountInSystemCurrency?.currency,
      totalDebtAmountExchangeRateDate:
        contract.totalDebtAmountExchangeRateDate ?? '',
      hasIncreasingInterests: contract.hasIncreasingInterests,
      phaseSegmentation: {
        phaseStatus: contract.contractStage,
        additionalCost: additionalCostType(
          contract.amountOutdated,
          contract.contractStage
        )
      },
      productData:
        contract.productData === null
          ? (null as any) // TODO: TO be fixed later, temporary solution to problem with incorrect type for productData
          : {
              documentBarcode: null as any, // TODO: unused, deprecated
              paymentHint: null as any, // TODO: unused, deprecated
              annexAvailable: null, // // annexAvailable (annex) taken from websocket, deprecated
              debtAmountOnSimulationDate:
                contract.productData.debtAmountOnSimulationDate?.amount ?? 0,
              simulationDate:
                contract.productData.productParameters.configurationDate,
              offerId: contract.productData.sourceProductId, // Assuming sourceProductId maps to offerId
              overdue: contract.productData.overdue,
              installments: mapInstallments(
                contract.productData.productParameters.installments
              ),
              productParameters: mapProductParameters(
                contract.productData.productParameters
              ) as any,
              productStatus: contract.productData.productStatus,
              simulationId: contract.productData.sourceProductId,
              simulationStatus: contract.productData.productStatus,
              SimulationStatusTextDescription: contract.productData
                ?.SimulationStatusTextDescription as SimulationStatusTextDescription,
              type: contract.productData.type,
              totalAmountPaidCurrentProduct:
                contract.productData?.totalAmountPaidCurrentProduct?.amount ??
                0,
              prerequisitePaymentLeft:
                contract.productData?.prerequisitePaymentLeft?.amount ?? 0
            }
    },
    details: {
      bigHistory: null as any, // TODO: bigHistory is only used from agreement details, deprecated
      originalDebtAmount: null as any, // TODO: originalDebtAmount is only used from agreement details, deprecated
      payments: [], // Missing in ContractDTO
      accountNumber: null as any, // TODO: unused, deprecated
      agreementId: contract.sourceContractId,
      agreementPhase: contract.contractStage,
      capitalByCurrency: contract.capitalByCurrency,
      costsByCurrency: contract.costsByCurrency,
      penaltiesByCurrency: contract.penaltiesByCurrency,
      creditType: contract.creditType,
      debtorRoles: contract.debtorRoles,
      interestByCurrency: contract.interestByCurrency,
      originalCreditor: contract.originalCreditorName,
      totalAmountPaidCurrentProduct:
        contract.productData?.totalAmountPaidCurrentProduct?.amount ?? 0
    }
  };
};

export const mapNewDetailsToDeprecated = (
  details: ContractDetails,
  t: TFunction<'translation'>
): Agreement => ({
  details: {
    // TODO: Verify accountNumber these fields exist in the new details
    accountNumber: null as any, // TODO: accountNumber is from a payment endpoint, deprecated
    agreementId: details.sourceContractId,
    agreementPhase: details.contractStage,
    originalCreditor: details.lenderName,
    payments: [], // Payments are from a different endpoint
    totalAmountPaidCurrentProduct:
      details.productData?.totalAmountPaidCurrentProduct?.amount ?? 0,
    bigHistory: mapBigHistory(details.economicInformation),
    creditType: t(`CONTRACT_TYPE_${details.creditType}`),
    debtorRoles: [t(`CONTRACT_CLIENT_ROLE_${details.debtorRole}`)],
    originalDebtAmount: details.originalDebtAmount,
    originalDebtAmountWithCurrency: details.originalDebtAmountWithCurrency,
    capitalByCurrency: details.capitalByCurrency,
    interestByCurrency: details.interestByCurrency,
    costsByCurrency: details.costsByCurrency,
    penaltiesByCurrency: details.penaltiesByCurrency
  },
  generalData: {
    offerAvailable: null, // offerAvailable (offer) taken from websocket, deprecated
    hasIncreasingInterests: null as any, // TODO: only used in offers, deprecated
    fromUgodowi: false, // TODO: fromUgodowi doesn't appear in contract endpoint, deprecated
    creditorId: null as any, // TODO: unused, deprecated
    lastSettledPayment: null as any, // TODO: Move to notifications endpoint
    overduePaymentsAmount:
      details.productData?.overduePaymentsAmount?.amount || 0,
    agreementId: details.sourceContractId,
    currency: details.currency ?? 'NONE',
    originalCreditorName: details.lenderName,
    currentCreditorName: details.lenderName,
    totalDebtAmountByCurrency: details.totalDebtAmountByCurrency ?? { NONE: 0 },
    totalDebtAmountInSystemCurrency:
      details.totalDebtAmountInSystemCurrency?.amount ?? 0,
    totalDebtAmountInSystemCurrencyValue:
      details.totalDebtAmountInSystemCurrency?.currency,
    waitingPaymentsAmountByCurrency: details.waitingPaymentsAmountByCurrency,
    totalDebtAmountExchangeRateDate:
      details.totalDebtAmountExchangeRateDate ?? '',
    totalDebtAmountWithoutRemissionByCurrency:
      details?.totalDebtAmountWithoutRemissionByCurrency,
    phaseSegmentation: {
      phaseStatus: details.contractStage,
      additionalCost: additionalCostType(
        details.amountOutdated,
        details.contractStage
      )
    },
    productData:
      details.productData === null
        ? // TODO: TO be fixed later, temporary solution to problem with incorrect type for productData
          (null as any)
        : {
            // TODO: Verify if these fields exist in the new details but under different names
            documentBarcode: null as any, // TODO: unused, deprecated
            paymentHint: null as any, // TODO: unused, deprecated
            SimulationStatusTextDescription: null as any, // Missing
            annexAvailable: null, // annexAvailable (annex) taken from websocket, deprecated
            simulationId: details.productData.sourceProductId,
            simulationStatus: details.productData.productStatus,
            offerId: details.productData.sourceProductId,
            productStatus: details.productData.productStatus,
            type: details.productData.type,
            simulationDate:
              details.productData.productParameters.configurationDate,
            productParameters: mapProductParameters(
              details.productData.productParameters
            ) as any,
            installments: mapInstallments(
              details.productData.productParameters.installments
            ),
            prerequisitePaymentLeft:
              details.productData.prerequisitePaymentLeft?.amount ?? 0,
            totalAmountPaidCurrentProduct:
              details.productData.totalAmountPaidCurrentProduct?.amount ?? 0,
            debtAmountOnSimulationDate:
              details.productData.debtAmountOnSimulationDate?.amount ?? 0,
            overdue: details.productData.overdue
          }
  }
});
